import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase'
import Swal from 'sweetalert2'
import TablaAccesorios from '../components/inventario/TablaAccesorios'
import ModalAccesorio from '../components/inventario/ModalAccesorio'

const emptyAccesorioForm = {
  categoria: '', nombre: '', compatibilidad: '', precioInput: '', moneda: 'USD', stock: '5'
}

export default function SeccionAccesorios({ toast, comercioId }) {
  const [search, setSearch] = useState('')
  const [modal, setModal] = useState(false)
  const [editId, setEditId] = useState(null)
  const [accesorios, setAccesorios] = useState([])
  const [formAccesorio, setFormAccesorio] = useState(emptyAccesorioForm)
  const [statsAccesorios, setStatsAccesorios] = useState({ totalTipos: 0, stockTotal: 0, bajoStock: 0 })

  const cargarAccesorios = useCallback(async () => {
    if (!comercioId) return
    const { data, error } = await supabase
      .from('inventario_accesorios')
      .select('*')
      .eq('comercio_id', comercioId)
      .order('id', { ascending: false })
    
    if (!error && data) {
      setAccesorios(data)
      setStatsAccesorios({
        totalTipos: data.length,
        stockTotal: data.reduce((acc, curr) => acc + (curr.stock || 0), 0),
        bajoStock: data.filter(x => x.stock <= 3).length
      })
    }
  }, [comercioId])

  useEffect(() => { 
    cargarAccesorios() 
  }, [cargarAccesorios])

  const abrirModal = (item = null) => {
    if (item) {
      let monedaGuardada = item.precio_ars > 0 ? 'ARS' : 'USD'
      let precioGuardado = item.precio_ars > 0 ? item.precio_ars : item.precio
      setFormAccesorio({
        ...item,
        precioInput: Number(precioGuardado).toLocaleString('es-AR'),
        moneda: monedaGuardada,
        stock: String(item.stock)
      })
      setEditId(item.id)
    } else {
      setFormAccesorio(emptyAccesorioForm)
      setEditId(null)
    }
    setModal(true)
  }

  const guardar = async (e) => {
    e.preventDefault()
    if (!comercioId) return toast('Error de autenticación', 'error')

    const numeroLimpio = String(formAccesorio.precioInput).replace(/\./g, '')
    const valorPrecio = numeroLimpio ? parseFloat(numeroLimpio) : 0
    if (valorPrecio <= 0) return toast('Ingresa un precio válido', 'error')

    const payload = {
      categoria: formAccesorio.categoria,
      nombre: formAccesorio.nombre,
      compatibilidad: formAccesorio.compatibilidad,
      precio: formAccesorio.moneda === 'USD' ? valorPrecio : 0,
      precio_ars: formAccesorio.moneda === 'ARS' ? valorPrecio : 0,
      stock: parseInt(formAccesorio.stock) || 0,
      comercio_id: comercioId
    }

    const { error } = editId
      ? await supabase.from('inventario_accesorios').update(payload).eq('id', editId)
      : await supabase.from('inventario_accesorios').insert([payload])

    if (error) return toast('Error al guardar accesorio', 'error')
    cargarAccesorios()
    toast('Guardado correctamente', 'success')
    setModal(false)
  }

  const eliminar = async (id) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: "¡No podrás revertir esto!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    })

    if (!result.isConfirmed) return

    const { error } = await supabase.from('inventario_accesorios').delete().eq('id', id)
    if (error) {
      toast('Error al eliminar', 'error')
    } else {
      toast('Eliminado correctamente', 'success')
      cargarAccesorios()
    }
  }

  const registrarVentaAccesorio = async (accesorio) => {
    if ((accesorio.stock || 0) <= 0) {
      return Swal.fire({ title: 'Sin Stock', text: 'No quedan unidades disponibles.', icon: 'error' })
    }

    const { value: cantidad } = await Swal.fire({
      title: 'Venta de Accesorio',
      width: '450px',
      html: `
        <div style="text-align: left; padding: 0 5px;">
          <div style="background: #f8fafc; border: 1px solid #e2e8f0; padding: 12px 16px; border-radius: 8px; margin-bottom: 20px;">
            <span style="font-size: 11px; color: #64748b; text-transform: uppercase; font-weight: 700;">Producto</span>
            <div style="font-size: 16px; font-weight: 700; color: #0f172a; margin-top: 4px;">${accesorio.nombre}</div>
            <div style="font-size: 12px; color: #64748b; margin-top: 2px;">Disponibles: ${accesorio.stock} u.</div>
          </div>
          <div style="margin-bottom: 16px;">
            <label style="display: block; font-size: 13px; font-weight: 600; color: #475569; margin-bottom: 6px;">Cantidad a vender</label>
            <input id="swal-input-cantidad" type="number" value="1" min="1" max="${accesorio.stock}" style="width: 100%; border: 1px solid #cbd5e1; border-radius: 8px; padding: 10px 12px;">
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Descontar Stock',
      confirmButtonColor: '#10b981',
      preConfirm: () => {
        const cant = parseInt(document.getElementById('swal-input-cantidad').value)
        if (isNaN(cant) || cant < 1) return Swal.showValidationMessage('Ingresa una cantidad válida.')
        if (cant > accesorio.stock) return Swal.showValidationMessage('No hay stock suficiente.')
        return cant
      }
    })

    if (!cantidad) return

    try {
      const { error } = await supabase
        .from('inventario_accesorios')
        .update({ stock: accesorio.stock - cantidad })
        .eq('id', accesorio.id)

      if (error) throw error
      toast('Stock actualizado', 'success')
      cargarAccesorios()
    } catch (err) {
      toast('Error al descontar stock', 'error')
    }
  }

  const accesoriosFiltrados = accesorios.filter(x => 
    x.nombre?.toLowerCase().includes(search.toLowerCase()) || 
    x.compatibilidad?.toLowerCase().includes(search.toLowerCase()) || 
    x.categoria?.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="animate-fade-in">
      <div className="inv-stats-grid">
        <div className="inv-stat-card total"><span className="inv-stat-label">Tipos de Accesorios</span><span className="inv-stat-value">{statsAccesorios.totalTipos}</span></div>
        <div className="inv-stat-card disponible"><span className="inv-stat-label">Unidades Totales</span><span className="inv-stat-value">{statsAccesorios.stockTotal} u.</span></div>
        <div className="inv-stat-card vendido"><span className="inv-stat-label">Bajo Stock (≤3 u.)</span><span className="inv-stat-value">{statsAccesorios.bajoStock}</span></div>
      </div>

      <div className="inv-card">
        <div className="inv-toolbar">
          <div className="inv-search-wrapper">
            <svg className="inv-search-icon" viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" strokeWidth="2" fill="none"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar funda, cargador, compatibilidad..." className="inv-search-input" />
          </div>
          <button onClick={() => abrirModal()} className="btn-inv-primary">+ Agregar Accesorio</button>
        </div>

        <TablaAccesorios 
          accesorios={accesoriosFiltrados} 
          onEdit={abrirModal} 
          onDelete={eliminar} 
          onVender={registrarVentaAccesorio} 
        />
      </div>

      {modal && (
        <ModalAccesorio formAccesorio={formAccesorio} setFormAccesorio={setFormAccesorio} guardar={guardar} setModal={setModal} editId={editId} />
      )}
    </div>
  )
}